import React from 'react';
import styled from 'styled-components';

import { colors } from '../../../../theme';
import {
  TextField,
  RadioField,
  SectionCategory,
  QuestionWrapper,
  QuestionGroup,
  QuestionText,
  Warning
} from '../../survey/Questions.style';
import {
  isAnswered,
  isQuestionVisible,
  isRequiredQuestion
} from './questionVisibility';

const TransparentQuestionWrapper = styled(QuestionWrapper)`
  background-color: transparent;
  box-shadow: none;
  padding: 0;
  margin: 0.75rem 0 0;

  & > div {
    margin: 0;
    padding: 0;
  }

  h4 {
    font-weight: 700;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  ${({ error }) =>
    error &&
    `
      && input,
      && textarea {
        border-color: ${colors.error};
      }
    `}

  && .answers {
    display: flex;
    flex-direction: column;
    gap: 4px;

    div {
      min-width: auto;
    }

    label {
      align-items: center;
      width: 100%;
    }

    p {
      padding-left: 12px;
    }

    label p {
      margin-bottom: 0px;
      max-width: fit-content;
    }

    input {
      height: 16px;
      width: 16px;
    }

    .checkmark {
      height: 16px;
      width: 16px;
      min-width: 16px;
      flex-shrink: 0;
      border: 1.5px solid ${colors.primary};
    }

    input:checked ~ .checkmark {
      border: 1.5px solid ${colors.primary};
      background-color: ${colors.white};
      position: relative;
    }

    input:checked ~ .checkmark::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      height: 12px;
      width: 12px;
      border-radius: 50%;
      background-color: ${colors.primary};
    }
  }
`;

const SectionHeader = styled(SectionCategory)`
  color: ${colors.gray};
  font-size: 1rem;
  font-weight: 500;
  text-transform: none;
  padding: 1.5rem 0 0.5rem 0;
`;

const renderInput = ({
  question,
  answers,
  onAnswer,
  setCurrentQuestion,
  nextQuestionID,
  showError
}) => {
  const { _id: id, text, helperText, options } = question;
  const inputType = question.questionType && question.questionType.desc;
  const isRequired = question.isRequired !== false;
  const value =
    answers[id] && answers[id].answer !== undefined ? answers[id].answer : '';

  const header = (
    <header>
      <QuestionText>{isRequired ? `* ${text}` : text}</QuestionText>
      {helperText && <p className="helpertext">{helperText}</p>}
    </header>
  );

  if (inputType === 'radio') {
    return (
      <RadioField>
        {header}
        <div className="answers">
          {(options || []).map(option => {
            const uniqueId = option + id;
            return (
              <div key={`${option}parent`}>
                <div key={`${option}child`}>
                  <label htmlFor={uniqueId}>
                    <input
                      value={option}
                      checked={value === option}
                      id={uniqueId}
                      name={id}
                      type="radio"
                      onChange={() => {
                        onAnswer(id, option);
                        setCurrentQuestion(nextQuestionID);
                      }}
                    />
                    <span className="checkmark" />
                    <p>{option}</p>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        {showError && <Warning>This is a required field</Warning>}
      </RadioField>
    );
  }

  if (inputType === 'number') {
    return (
      <TextField>
        {header}
        <div>
          <input
            id={`input-${id}`}
            name={id}
            type="number"
            min="0"
            value={value}
            onChange={e => onAnswer(id, e.target.value)}
            onBlur={() => setCurrentQuestion(nextQuestionID)}
          />
        </div>
        {showError && <Warning>This is a required field</Warning>}
      </TextField>
    );
  }

  return (
    <TextField>
      {header}
      <div>
        <textarea
          id={`input-${id}`}
          name={id}
          rows="3"
          value={value}
          onChange={e => onAnswer(id, e.target.value)}
          onBlur={() => setCurrentQuestion(nextQuestionID)}
        />
      </div>
      {showError && <Warning>This is a required field</Warning>}
    </TextField>
  );
};

export default class TrainerSurveyQs extends React.Component {
  state = { currentQuestion: null };

  componentDidUpdate() {
    const { currentQuestion } = this.state;
    if (!currentQuestion || currentQuestion === 'end') return;
    const element = document.getElementById(currentQuestion);
    if (element && 'scrollBehavior' in document.documentElement.style) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }

  setCurrentQuestion = questionId => {
    this.setState({ currentQuestion: questionId });
  };

  render() {
    const { questions, answers, onAnswer, submitAttempted } = this.props;

    const visibleQuestions = questions.filter(question =>
      isQuestionVisible(question, questions, answers)
    );

    const sections = {};
    visibleQuestions.forEach(question => {
      const groupText = (question.group && question.group.text) || '';
      if (!sections[groupText]) sections[groupText] = [];
      sections[groupText].push(question);
    });

    const blockingIndex = visibleQuestions.findIndex(
      question =>
        isRequiredQuestion(question) && !isAnswered(answers, question._id)
    );

    let flatIndex = -1;

    return (
      <QuestionGroup>
        {Object.keys(sections).map(groupName => (
          <QuestionGroup key={groupName || 'general'}>
            {groupName !== '' && <SectionHeader>{groupName}</SectionHeader>}
            {sections[groupName].map(question => {
              flatIndex += 1;
              const currentFlatIndex = flatIndex;
              const disabled =
                blockingIndex !== -1 && currentFlatIndex > blockingIndex;
              const nextQuestion = visibleQuestions[currentFlatIndex + 1];
              const nextQuestionID = nextQuestion ? nextQuestion._id : 'end';
              const showError =
                submitAttempted &&
                isRequiredQuestion(question) &&
                !isAnswered(answers, question._id);
              return (
                <TransparentQuestionWrapper
                  key={question._id}
                  id={question._id}
                  disabled={disabled}
                  error={showError}
                >
                  {renderInput({
                    question,
                    answers,
                    onAnswer,
                    setCurrentQuestion: this.setCurrentQuestion,
                    nextQuestionID,
                    showError
                  })}
                </TransparentQuestionWrapper>
              );
            })}
          </QuestionGroup>
        ))}
      </QuestionGroup>
    );
  }
}
